import { Button, Dropdown, Modal, Space, Tag } from "antd"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { PageCard } from "src/common/styles/styles"
import { Quotation, QuotationtStatus } from "src/common/models/quotation"
import { Urls } from "src/common/urls"
import { formatToCurrency } from "src/common/util"
import { SearchableTable } from "src/components/SearchableTable"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { cleanCurrentQuotation, getQuotationsApi, updateQuotationStatusApi } from "../state/action"
import { RootState } from "src/state/reducer"
import { CheckOutlined, CloseOutlined, DeleteOutlined, DownOutlined, EyeOutlined, PlusOutlined, RollbackOutlined } from '@ant-design/icons'
import { SystemDescriptions } from "src/common/descriptions"
import { QuotationPrintAction, QuotationPrintModal } from "../CreateQuotation/QuotationPrintModal"

export const QuotationRecords = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const descriptions = SystemDescriptions.PAGES.QUOTATION.CREATE.RECORDS

    const quotations: Quotation[] = useSelector((state: RootState) => state.quotation.quotations || [])
    const [printAction, setPrintAction] = useState<{
        action: QuotationPrintAction
        quotation: Quotation
    } | undefined>(undefined)

    useEffect(() => {
        dispatch(getQuotationsApi())
    }, [dispatch])

    const statusLabelByStatus = useMemo(() => ({
        [QuotationtStatus.DRAFT]: descriptions.STATUS_VALUES.DRAFT,
        [QuotationtStatus.SENT]: descriptions.STATUS_VALUES.SENT,
        [QuotationtStatus.APPROVED]: descriptions.STATUS_VALUES.APPROVED,
        [QuotationtStatus.REJECTED]: descriptions.STATUS_VALUES.REJECTED,
        [QuotationtStatus.DELETED]: descriptions.STATUS_VALUES.DELETED,
    }), [descriptions])

    const columns: any = [
        {
            title: descriptions.COLUMNS.CODE,
            dataIndex: "code",
            key: "code",
            align: searchableTableUtils.alignment.centerAlign,
        },
        {
            title: descriptions.COLUMNS.NAME,
            dataIndex: "name",
            key: "name",
            align: searchableTableUtils.alignment.centerAlign,
        },
        {
            title: descriptions.COLUMNS.CUSTOMER,
            dataIndex: "customerName",
            key: "customerName",
            align: searchableTableUtils.alignment.centerAlign,
        },
        {
            title: descriptions.COLUMNS.STATUS,
            dataIndex: "status",
            key: "status",
            align: searchableTableUtils.alignment.centerAlign,
            render: (value: QuotationtStatus) => {
                const colorByStatus = {
                    [QuotationtStatus.DRAFT]: "default",
                    [QuotationtStatus.SENT]: "processing",
                    [QuotationtStatus.APPROVED]: "success",
                    [QuotationtStatus.REJECTED]: "error",
                    [QuotationtStatus.DELETED]: "warning",
                }

                return <Tag color={colorByStatus[value]}>{statusLabelByStatus[value]}</Tag>
            },
        },
        {
            title: descriptions.COLUMNS.TOTAL,
            dataIndex: "total",
            key: "total",
            align: searchableTableUtils.alignment.centerAlign,
            render: (value) => formatToCurrency(value?.toString() || "0"),
        },
        {
            title: descriptions.COLUMNS.ACTIONS,
            dataIndex: "actions",
            key: "actions",
            align: searchableTableUtils.alignment.centerAlign,
            render: (_: any, record: Quotation) => {
                const handleViewClick = () => {
                    navigate(Urls.FRONTEND.QUOTATION.EDIT_PAGE(record.id!))
                }

                const openStatusConfirm = (status: QuotationtStatus, confirmTitle: string, okText: string) => {
                    Modal.confirm({
                        title: confirmTitle,
                        okText,
                        cancelText: descriptions.CONFIRM.CANCEL,
                        onOk: () => {
                            dispatch(updateQuotationStatusApi(record.id!, status))
                        },
                    })
                }

                const items = [
                    {
                        key: "view",
                        icon: <EyeOutlined />,
                        label: descriptions.ACTIONS.VIEW,
                        onClick: handleViewClick,
                    },
                    {
                        key: "pdf",
                        label: descriptions.ACTIONS.GENERATE_PDF,
                        disabled: record.status === QuotationtStatus.DELETED,
                        onClick: () => setPrintAction({
                            action: QuotationPrintAction.PDF,
                            quotation: record,
                        }),
                    },
                    {
                        key: "telegram",
                        label: descriptions.ACTIONS.SEND_TELEGRAM,
                        disabled: record.status === QuotationtStatus.DELETED,
                        onClick: () => setPrintAction({
                            action: QuotationPrintAction.TELEGRAM,
                            quotation: record,
                        }),
                    },
                    {
                        key: "approve",
                        icon: <CheckOutlined />,
                        label: descriptions.ACTIONS.APPROVE,
                        disabled: record.status === QuotationtStatus.APPROVED || record.status === QuotationtStatus.DELETED,
                        onClick: () => openStatusConfirm(QuotationtStatus.APPROVED, descriptions.CONFIRM.APPROVE, descriptions.CONFIRM.OK_APPROVE),
                    },
                    {
                        key: "reject",
                        icon: <CloseOutlined />,
                        label: descriptions.ACTIONS.REJECT,
                        disabled: record.status === QuotationtStatus.REJECTED || record.status === QuotationtStatus.DELETED,
                        onClick: () => openStatusConfirm(QuotationtStatus.REJECTED, descriptions.CONFIRM.REJECT, descriptions.CONFIRM.OK_REJECT),
                    },
                    {
                        key: "draft",
                        icon: <RollbackOutlined />,
                        label: descriptions.ACTIONS.MARK_DRAFT,
                        disabled: record.status === QuotationtStatus.DRAFT || record.status === QuotationtStatus.DELETED,
                        onClick: () => openStatusConfirm(QuotationtStatus.DRAFT, descriptions.CONFIRM.MARK_DRAFT, descriptions.CONFIRM.OK_MARK_DRAFT),
                    },
                    {
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: descriptions.ACTIONS.DELETE,
                        danger: true,
                        disabled: record.status === QuotationtStatus.DELETED,
                        onClick: () => openStatusConfirm(QuotationtStatus.DELETED, descriptions.CONFIRM.DELETE, descriptions.CONFIRM.OK_DELETE),
                    },
                ]

                return (
                    <Dropdown
                        menu={{ items }}
                        trigger={["click"]}
                    >
                        <Button size="small">
                            <Space>
                                {descriptions.ACTIONS.MENU}
                                <DownOutlined />
                            </Space>
                        </Button>
                    </Dropdown>
                )
            },
        },
    ]

    return (
        <PageCard
            size="small"
            title={descriptions.TITLE}
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        dispatch(cleanCurrentQuotation())
                        navigate(Urls.FRONTEND.QUOTATION.CREATE)
                    }}
                >
                    {descriptions.NEW_QUOTATION}
                </Button>
            }
        >
            <SearchableTable
                showSearch
                searchLabel={descriptions.SEARCH}
                showLoader={false}
                items={quotations}
                tableColumns={columns}
            />
            <QuotationPrintModal
                open={!!printAction}
                action={printAction?.action || QuotationPrintAction.PDF}
                quotationId={printAction?.quotation.id}
                quotationCode={printAction?.quotation.code}
                quotationName={printAction?.quotation.name}
                onCancel={() => setPrintAction(undefined)}
            />
        </PageCard>
    )
}
